import { mintTfExchangeToken, TfTokenResponse } from 'network/Token';
import { ddlog } from 'utils/miscellaneous';

export enum AUTH_METHOD {
  MSAL = 'tf-azure',
  SCHOOL = 'school',
}
export const AUTH_METHOD_KEY = 'x-token-issuer';
const AUTH_TOKEN_KEY = 'school-token';
const SCHOOL_REDIRECT_URL_KEY = 'school-redirect-url';
const TOKEN_EXPIRY_BUFFER_S = 60;
const MIN_REFRESH_INTERVAL_MS = 1000;

const hasSessionStorage = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

/**
 * Builds a manager for a minted auth token: it invokes `mint` to obtain the token,
 * caches it in sessionStorage, and automatically refreshes it before expiry. Each
 * token gets its own storage key, update event, and independent refresh schedule.
 * The `mint` function decides which endpoint/audience the token comes from.
 */
const createMintedTokenManager = (
  label: string,
  mint: () => Promise<TfTokenResponse>,
  storageKey: string,
  updatedEvent: string,
) => {
  let refreshScheduled = false;
  let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const get = (): string | null => {
    if (!hasSessionStorage()) {
      return null;
    }

    try {
      return window.sessionStorage.getItem(storageKey) || null;
    } catch (error) {
      ddlog.warn(`Failed to get ${label} auth token`, { error });
      return null;
    }
  };

  const set = (token: string) => {
    if (!hasSessionStorage()) {
      return;
    }

    try {
      window.sessionStorage.setItem(storageKey, token);
    } catch (error) {
      ddlog.warn(`Failed to store ${label} auth token`, { error });
    }
  };

  /**
   * Mints the token, stores it in sessionStorage, and schedules an automatic refresh
   * before expiry. On failure, stores nothing and ignores silently — the caller is
   * responsible for retrying.
   */
  const init = async (): Promise<void> => {
    if (refreshScheduled) {
      return;
    }
    refreshScheduled = true;

    try {
      const response = await mint();
      set(response.access_token);
      window.dispatchEvent(new CustomEvent(updatedEvent, { detail: { token: response.access_token } }));

      const refreshDelayMs = Math.max((response.expires_in - TOKEN_EXPIRY_BUFFER_S) * 1000, MIN_REFRESH_INTERVAL_MS);
      refreshTimeoutId = setTimeout(() => {
        refreshTimeoutId = null;
        refreshScheduled = false;
        init();
      }, refreshDelayMs);
    } catch (error) {
      refreshScheduled = false;
      ddlog.warn(`Failed to mint ${label} auth token`, { error });
    }
  };

  /**
   * Clears the cached token from sessionStorage and cancels any pending refresh.
   */
  const clearStored = () => {
    if (refreshTimeoutId !== null) {
      clearTimeout(refreshTimeoutId);
      refreshTimeoutId = null;
    }
    refreshScheduled = false;

    if (!hasSessionStorage()) {
      return;
    }

    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (error) {
      ddlog.warn(`Failed to clear ${label} auth token`, { error });
    }
  };

  return { updatedEvent, get, set, init, clearStored };
};

// Chatbot keeps its original GAP token (POST /token) — behavior unchanged.
const chatbotTokenManager = createMintedTokenManager(
  'chatbot',
  () => mintTfExchangeToken(['b2s-chatbot']),
  'chatbot-auth-token',
  'chatbotAuthTokenUpdated',
);
// Compass uses an audience-scoped token minted via POST /token/exchange.
const compassTokenManager = createMintedTokenManager(
  'compass',
  () => mintTfExchangeToken(['tf-compass', 'tf-api']),
  'compass-auth-token',
  'compassAuthTokenUpdated',
);

/**
 * Auth token used exclusively for the B2S chatbot (MSAL users only).
 */
export const CHATBOT_AUTH_TOKEN_UPDATED_EVENT = chatbotTokenManager.updatedEvent;
export const getChatbotAuthToken = chatbotTokenManager.get;
export const setChatbotAuthToken = chatbotTokenManager.set;
export const initChatbotAuthToken = chatbotTokenManager.init;

/**
 * Auth token used exclusively for Compass (MSAL users only).
 */
export const getCompassAuthToken = compassTokenManager.get;
export const initCompassAuthToken = compassTokenManager.init;

/**
 * Gets the stored authentication method from sessionStorage.
 * Used to determine which auth flow to use on page refresh.
 *
 * @returns The stored auth method or null if not found
 */
export const getStoredAuthMethod = (): AUTH_METHOD | null => {
  if (!hasSessionStorage()) {
    return null;
  }

  try {
    const method = window.sessionStorage.getItem(AUTH_METHOD_KEY);
    return method === AUTH_METHOD.SCHOOL ? AUTH_METHOD.SCHOOL : method === AUTH_METHOD.MSAL ? AUTH_METHOD.MSAL : null;
  } catch (error) {
    ddlog.warn('Failed to get stored auth method', { error });
    return null;
  }
};

/**
 * Stores the authentication method in sessionStorage.
 * Called after successful authentication to remember the method used.
 *
 * @param method - The auth method to store
 */
export const setStoredAuthMethod = (method: AUTH_METHOD) => {
  if (!hasSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.setItem(AUTH_METHOD_KEY, method);
  } catch (error) {
    ddlog.warn('Failed to store auth method', { error });
  }
};

/**
 * Clears the stored authentication method from sessionStorage.
 * Should be called on logout to ensure clean state.
 */
export const clearStoredAuthMethod = () => {
  if (!hasSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(AUTH_METHOD_KEY);
  } catch (error) {
    ddlog.warn('Failed to clear auth method', { error });
  }
};

/**
 * Returns the stored authentication token from sessionStorage.
 *
 * @returns the stored auth token or null if not found
 */
export const getStoredAuthToken = (): string | null => {
  if (!hasSessionStorage()) {
    return null;
  }

  try {
    return window.sessionStorage.getItem(AUTH_TOKEN_KEY) || null;
  } catch (error) {
    ddlog.warn('Failed to get stored auth token', { error });
    return null;
  }
};

/**
 * Stores the authentication token in sessionStorage.
 * Called after successful authentication to remember the token used.
 *
 * @param token - The auth token to store
 */
export const setStoredAuthToken = (token: string) => {
  if (!hasSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch (error) {
    ddlog.warn('Failed to store auth token', { error });
  }
};

/**
 * Clears the stored authentication token and all minted audience tokens from sessionStorage.
 * Should be called on logout to ensure clean state.
 * Also cancels any pending token refresh timeouts.
 */
export const clearStoredAuthToken = () => {
  chatbotTokenManager.clearStored();
  compassTokenManager.clearStored();

  if (!hasSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
  } catch (error) {
    ddlog.warn('Failed to clear auth token', { error });
  }
};

export const setSchoolRedirectUrl = (url: string) => {
  if (!hasSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.setItem(SCHOOL_REDIRECT_URL_KEY, url);
  } catch (error) {
    ddlog.warn('Failed to set school redirect url', { error });
  }
};

export const getSchoolRedirectUrl = (): string | null => {
  if (!hasSessionStorage()) {
    return null;
  }

  try {
    return window.sessionStorage.getItem(SCHOOL_REDIRECT_URL_KEY);
  } catch (error) {
    ddlog.warn('Failed to get school redirect url', { error });
    return null;
  }
};

export const redirectOrClose = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const schoolRedirectUrl = getSchoolRedirectUrl();
  if (schoolRedirectUrl) {
    window.location.href = schoolRedirectUrl;
  } else {
    window.close();
  }
};

/**
 * Opens a URL in a new tab with school authentication token passed via hash parameter if available.
 * If a token exists, it will be passed to ensure the new tab has the necessary authentication context.
 * The AuthProvider will automatically read the token from the hash and store it in sessionStorage.
 *
 * Pass `finalUrlPromise` when the eventual destination depends on an async call: the tab opens
 * immediately on `url` (so the browser treats the open as user-initiated), then re-navigates to
 * the resolved URL when the promise settles.
 *
 * @param url - The URL path to open (e.g., '/offline/class/123')
 * @param tab - The target tab name (default '_blank')
 * @param finalUrlPromise - Optional. If provided, the tab navigates to its resolved URL once ready.
 */
export const openAuthenticatedTab = (
  url: string,
  tab: string = '_blank',
  finalUrlPromise?: Promise<string>,
): Window | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const token = getStoredAuthToken();
  const buildUrl = (target: string) => (token ? `${target}#token=${encodeURIComponent(token)}` : target);

  const newTab = window.open(buildUrl(url), tab);
  if (!newTab) {
    ddlog.warn('Failed to open new tab - popup may have been blocked', { url });
    return null;
  }

  if (finalUrlPromise) {
    finalUrlPromise.then(
      (finalUrl) => {
        newTab.location.href = buildUrl(finalUrl);
      },
      (error) => {
        ddlog.warn('Failed to resolve final URL for new tab', { error });
        newTab.close();
      },
    );
  }

  return newTab;
};
